import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  Building2,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  FileText,
  FolderOpen,
  HardHat,
  Inbox,
  LayoutDashboard,
  Lock,
  MailWarning,
  ShieldCheck,
  Sparkles,
  Users,
  Wrench
} from 'lucide-react';

const problemCards = [
  {
    title: 'COIs buried in email',
    text: 'Vendor documents get scattered across inboxes, attachments, and shared folders.',
    icon: Inbox
  },
  {
    title: 'Expiration dates are easy to miss',
    text: 'A spreadsheet only works if someone remembers to update it and follow up on time.',
    icon: Clock3
  },
  {
    title: 'No clear compliance snapshot',
    text: 'It is hard to quickly see which vendors are active, expired, pending, or expiring soon.',
    icon: ClipboardCheck
  },
  {
    title: 'Risk shows up too late',
    text: 'Teams often discover expired insurance only when work is already scheduled or underway.',
    icon: AlertTriangle
  }
];

const featureBlocks = [
  {
    title: 'Vendor records',
    text: 'Keep vendor contact details, status, and insurance history together.',
    icon: Building2
  },
  {
    title: 'Policy expiration tracking',
    text: 'Track active, pending, expired, and soon-expiring policies automatically.',
    icon: ShieldCheck
  },
  {
    title: 'Document uploads',
    text: 'Store policy documents where your team can actually find them.',
    icon: FolderOpen
  },
  {
    title: 'Expiration reminders',
    text: 'Receive in-app and email reminders before policies expire.',
    icon: Bell
  },
  {
    title: 'Team access',
    text: 'Invite teammates so everyone works from the same vendor insurance record.',
    icon: Users
  },
  {
    title: 'Simple vendor-based pricing',
    text: 'Start free and upgrade as your active vendor list grows.',
    icon: Sparkles
  }
];

const audienceCards = [
  {
    title: 'Property managers',
    text: 'Track COIs for maintenance vendors, contractors, cleaners, landscapers, and other service providers.',
    icon: Building2
  },
  {
    title: 'Contractors and construction teams',
    text: 'Keep subcontractor policies organized before work begins.',
    icon: HardHat
  },
  {
    title: 'Operations and business owners',
    text: 'Replace manual insurance tracking with a repeatable process your team can follow.',
    icon: Wrench
  }
];

const workflowSteps = [
  {
    title: 'Add vendors',
    text: 'Create vendor records for companies, subcontractors, suppliers, or your own business.'
  },
  {
    title: 'Attach policies',
    text: 'Record policy details, expiration dates, insurance types, and documents.'
  },
  {
    title: 'Track status',
    text: 'See which policies are active, pending, expired, or expiring soon.'
  },
  {
    title: 'Get reminded',
    text: 'Receive alerts 30 days, 14 days, and on the day important policies expire.'
  }
];

const pricingTiers = [
  {
    name: 'Free',
    price: '$0',
    description: 'Good for trying the workflow',
    features: ['Up to 5 active vendors', 'Policy expiration alerts', 'In-app notifications'],
    cta: 'Start free',
    highlighted: false
  },
  {
    name: 'Professional',
    price: '$49',
    period: '/month',
    description: 'Good for growing teams',
    features: ['Up to 50 active vendors', 'Everything in Free', 'Custom insurance types and fields'],
    cta: 'Start free',
    highlighted: true
  },
  {
    name: 'Unlimited',
    price: '$99',
    period: '/month',
    description: 'Good for larger vendor networks',
    features: ['Unlimited active vendors', 'Everything in Professional', 'Advanced reporting and API access'],
    cta: 'Start free',
    highlighted: false
  }
];

const trustPoints = [
  'Team-based workspaces',
  'Secure user authentication',
  'Owner-managed team access',
  'Policy document upload support',
  'Email and in-app notifications',
  'Stripe-powered subscription management',
  'Public demo available before signup'
];

function SectionHeader({ eyebrow, title, children, centered = false, inverse = false }) {
  return (
    <div className={centered ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}>
      {eyebrow && (
        <p className={`mb-3 text-sm font-semibold uppercase tracking-wide ${inverse ? 'text-primary-300' : 'text-primary-700'}`}>
          {eyebrow}
        </p>
      )}
      <h2 className={`text-3xl font-bold tracking-normal sm:text-4xl ${inverse ? 'text-white' : 'text-gray-950'}`}>
        {title}
      </h2>
      {children && (
        <p className={`mt-4 text-lg leading-8 ${inverse ? 'text-gray-300' : 'text-gray-600'}`}>
          {children}
        </p>
      )}
    </div>
  );
}

function ProductMockup() {
  const upcoming = [
    ['Metro Electrical', 'General Liability', '14 days', 'warning'],
    ['Summit Roofing', 'Workers Comp', 'Expired', 'danger'],
    ['Clearview Cleaning', 'Auto Liability', '30 days', 'success']
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-xl shadow-gray-200/70">
      <div className="mb-5 flex items-center justify-between border-b border-gray-100 pb-4">
        <div>
          <p className="text-sm font-medium text-gray-500">PolicyNotify dashboard</p>
          <h3 className="mt-1 text-lg font-semibold text-gray-950">Insurance status overview</h3>
        </div>
        <div className="rounded-lg bg-primary-50 p-2 text-primary-700">
          <LayoutDashboard className="h-5 w-5" aria-hidden="true" />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-green-100 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-700">Active policies</p>
          <p className="mt-2 text-3xl font-bold text-green-950">42</p>
        </div>
        <div className="rounded-lg border border-yellow-100 bg-yellow-50 p-4">
          <p className="text-sm font-medium text-yellow-700">Expiring soon</p>
          <p className="mt-2 text-3xl font-bold text-yellow-950">7</p>
        </div>
        <div className="rounded-lg border border-red-100 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">Expired policies</p>
          <p className="mt-2 text-3xl font-bold text-red-950">2</p>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 p-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900">Vendor usage</p>
          <p className="text-sm text-gray-500">38 / 50 active vendors</p>
        </div>
        <div className="h-2 rounded-full bg-gray-100">
          <div className="h-2 w-3/4 rounded-full bg-primary-600" />
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
          Upcoming expirations
        </div>
        <div className="divide-y divide-gray-100">
          {upcoming.map(([vendor, type, timing, status]) => (
            <div key={vendor} className="grid grid-cols-[1fr_auto] gap-4 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{vendor}</p>
                <p className="text-sm text-gray-500">{type}</p>
              </div>
              <span
                className={`self-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                  status === 'danger'
                    ? 'bg-red-100 text-red-700'
                    : status === 'warning'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-700'
                }`}
              >
                {timing}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <main className="min-h-screen bg-white text-gray-950">
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
        <nav className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center">
            <img
              src={`${process.env.PUBLIC_URL}/branding/logo_full_web.png`}
              alt="PolicyNotify"
              className="h-10 w-auto"
            />
          </Link>

          <div className="hidden items-center gap-7 text-sm font-medium text-gray-700 md:flex">
            <Link to="/demo" className="hover:text-gray-950">Demo</Link>
            <a href="#pricing" className="hover:text-gray-950">Pricing</a>
            <Link to="/login" className="hover:text-gray-950">Login</Link>
            <Link to="/signup" className="btn-primary px-4 py-2 text-sm">
              Start free
            </Link>
          </div>

          <div className="grid basis-full grid-cols-4 items-center gap-2 text-center text-sm font-medium text-gray-700 md:hidden">
            <Link to="/demo" className="rounded-lg px-2 py-2 hover:bg-gray-50 hover:text-gray-950">Demo</Link>
            <a href="#pricing" className="rounded-lg px-2 py-2 hover:bg-gray-50 hover:text-gray-950">Pricing</a>
            <Link to="/login" className="rounded-lg px-2 py-2 hover:bg-gray-50 hover:text-gray-950">Login</Link>
            <Link to="/signup" className="btn-primary px-2 py-2 text-sm">
              Start free
            </Link>
          </div>
        </nav>
      </header>

      <section className="border-b border-gray-100 bg-gray-50">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-6 sm:py-20 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-24">
          <div>
            <p className="mb-5 inline-flex rounded-full border border-primary-100 bg-white px-3 py-1 text-sm font-medium text-primary-700 shadow-sm">
              Vendor insurance tracking without spreadsheet chasing
            </p>
            <h1 className="max-w-4xl text-4xl font-bold tracking-normal text-gray-950 sm:text-5xl lg:text-6xl">
              Track vendor COIs before they become a problem
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">
              PolicyNotify helps teams manage vendor insurance documents, policy expirations, and
              renewal reminders in one simple workspace. No more spreadsheet chasing.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/demo" className="btn-primary btn-lg">
                Try the demo
              </Link>
              <Link to="/signup" className="btn-secondary btn-lg">
                Start free
              </Link>
            </div>

            <p className="mt-4 text-sm font-medium text-gray-500">
              Free for up to 5 active vendors. No complex setup required.
            </p>
          </div>

          <ProductMockup />
        </div>
      </section>

      <section className="px-5 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeader title="COI tracking gets messy fast">
            Many teams start with spreadsheets, inboxes, calendar reminders, and shared folders.
            That works for a few vendors, but it breaks down as the vendor list grows.
          </SectionHeader>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {problemCards.map(({ title, text, icon: Icon }) => (
              <div key={title} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-5 inline-flex rounded-lg bg-gray-100 p-3 text-gray-700">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-gray-950">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 px-5 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeader title="One place for vendors, policies, documents, and reminders">
            PolicyNotify gives your team a centralized workspace for vendor insurance records.
            Add vendors, attach policies, upload documents, and get notified before coverage expires.
          </SectionHeader>

          <div className="mt-10 grid gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Vendor record</p>
                  <h3 className="mt-1 text-xl font-semibold text-gray-950">Summit Roofing Co.</h3>
                </div>
                <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                  Needs follow-up
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contact</p>
                  <p className="mt-2 text-sm font-medium text-gray-900">operations@summit.example</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Documents</p>
                  <p className="mt-2 text-sm font-medium text-gray-900">3 policy files stored</p>
                </div>
                <div className="rounded-lg bg-green-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-green-700">General liability</p>
                  <p className="mt-2 text-sm font-medium text-green-950">Active through Aug 12</p>
                </div>
                <div className="rounded-lg bg-red-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Workers comp</p>
                  <p className="mt-2 text-sm font-medium text-red-950">Expired yesterday</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
                <div>
                  <h3 className="font-semibold text-gray-950">Replace scattered follow-up</h3>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Keep vendor details, policy status, documents, and renewal reminders tied to one record.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <Bell className="mt-1 h-5 w-5 shrink-0 text-primary-700" aria-hidden="true" />
                <div>
                  <h3 className="font-semibold text-gray-950">Know what needs attention</h3>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    See active, pending, expired, and expiring-soon policies before work depends on them.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeader title="Features for practical COI tracking">
            Everything your team needs to keep vendor insurance records organized and easy to review.
          </SectionHeader>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {featureBlocks.map(({ title, text, icon: Icon }) => (
              <div key={title} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-5 inline-flex rounded-lg bg-primary-50 p-3 text-primary-700">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-gray-950">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeader title="Built for teams responsible for vendor insurance" centered>
            PolicyNotify is designed for teams that need a practical way to keep vendor insurance
            records organized without adopting a heavy enterprise compliance platform.
          </SectionHeader>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {audienceCards.map(({ title, text, icon: Icon }) => (
              <div key={title} className="rounded-lg border border-gray-200 bg-white p-7 text-center shadow-sm">
                <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-gray-950">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-950 px-5 py-20 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeader title="A simple workflow your team can actually keep up with" inverse>
            Keep every vendor insurance record moving through the same practical process.
          </SectionHeader>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {workflowSteps.map(({ title, text }, index) => (
              <div key={title} className="rounded-lg border border-white/10 bg-white/5 p-6">
                <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-950">
                  <span className="text-sm font-bold">{index + 1}</span>
                </div>
                <h3 className="text-lg font-semibold">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-gray-300">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-lg border border-primary-100 bg-primary-50 p-8 shadow-sm sm:p-10 lg:flex lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary-700">
              Public demo
            </p>
            <h2 className="text-3xl font-bold text-gray-950 sm:text-4xl">
              See PolicyNotify before creating an account
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-700">
              Open the demo workspace to explore vendors, policies, dashboards, and notifications
              without signing up.
            </p>
          </div>
          <Link to="/demo" className="btn-primary btn-lg mt-8 shrink-0 lg:mt-0">
            Open demo
          </Link>
        </div>
      </section>

      <section id="pricing" className="bg-gray-50 px-5 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeader title="Start small. Upgrade when your vendor list grows." centered>
            PolicyNotify pricing is based on active vendors, so your plan can grow with your business.
          </SectionHeader>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {pricingTiers.map(({ name, price, period, description, features, cta, highlighted }) => (
              <div
                key={name}
                className={`rounded-lg border bg-white p-7 shadow-sm ${
                  highlighted ? 'border-primary-300 ring-2 ring-primary-100' : 'border-gray-200'
                }`}
              >
                {highlighted && (
                  <p className="mb-4 inline-flex rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700">
                    Popular
                  </p>
                )}
                <h3 className="text-xl font-bold text-gray-950">{name}</h3>
                <p className="mt-2 text-sm text-gray-600">{description}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="text-4xl font-bold text-gray-950">{price}</span>
                  {period && <span className="pb-1 text-sm font-medium text-gray-500">{period}</span>}
                </div>
                <ul className="mt-6 space-y-3">
                  {features.map((feature) => (
                    <li key={feature} className="flex gap-3 text-sm text-gray-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`mt-7 w-full ${highlighted ? 'btn-primary' : 'btn-secondary'}`}
                >
                  {cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-3xl text-center text-sm text-gray-600">
            Vendor limits are based on active vendors. Deactivated vendors do not count toward your active vendor limit.
          </p>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <SectionHeader title="Designed for practical insurance tracking">
            PolicyNotify helps teams track vendor insurance records, reduce missed expirations,
            and keep documents organized without overclaiming what software can guarantee.
          </SectionHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            {trustPoints.map((point) => (
              <div key={point} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
                <span className="text-sm font-medium text-gray-800">{point}</span>
              </div>
            ))}
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <Lock className="h-5 w-5 shrink-0 text-primary-700" aria-hidden="true" />
              <span className="text-sm font-medium text-gray-800">Built around controlled team access</span>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <MailWarning className="h-5 w-5 shrink-0 text-yellow-600" aria-hidden="true" />
              <span className="text-sm font-medium text-gray-800">Reminder windows for renewal follow-up</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-950 px-5 py-20 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <FileText className="mx-auto mb-6 h-10 w-10 text-primary-300" aria-hidden="true" />
          <h2 className="text-3xl font-bold sm:text-4xl">
            Ready to stop chasing vendor COIs?
          </h2>
          <p className="mt-4 text-lg leading-8 text-gray-300">
            Start with a free workspace or explore the demo to see how PolicyNotify keeps vendor
            insurance records organized.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/signup" className="btn-primary btn-lg">
              Start free
            </Link>
            <Link to="/demo" className="btn bg-white text-gray-950 hover:bg-gray-100 focus:ring-white btn-lg">
              Try the demo
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-200 bg-white px-5 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <img
              src={`${process.env.PUBLIC_URL}/branding/logo_full_web.png`}
              alt="PolicyNotify"
              className="h-10 w-auto"
            />
            <p className="mt-4 max-w-xl text-sm leading-6 text-gray-600">
              PolicyNotify helps teams track vendor COIs, policy documents, and expiration reminders in one simple workspace.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium text-gray-700">
            <Link to="/demo" className="hover:text-gray-950">Demo</Link>
            <a href="#pricing" className="hover:text-gray-950">Pricing</a>
            <Link to="/login" className="hover:text-gray-950">Login</Link>
            <Link to="/signup" className="hover:text-gray-950">Start free</Link>
            <a href="/terms" className="hover:text-gray-950">Terms</a>
            <a href="/privacy" className="hover:text-gray-950">Privacy</a>
          </div>
        </div>
      </footer>
    </main>
  );
}
